import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'

export type StatusBadgeVariant =
  | 'unsubmitted' // 未提出
  | 'generating' // 作成中
  | 'creationFailed' // 作成失敗（計画生成失敗、要再作成）
  | 'rejected' // 差戻し（要再作成）
  | 'submitted' // 提出済
  | 'accepted' // 受領済み
  | 'inconsistent' // 不整合
  | 'submitting' // 処理中（提出 enqueue 済み、worker の OCCTO put 完了待ち）
  | 'approvalPending' // 承認待ち（承認完了後に OCCTO へ提出）
  | 'submitFailed' // 提出失敗（提出起因。XML は生成済みのため再提出で復旧）
  | 'sendFailed' // 送信失敗（OCCTO 未到達。ユーザーが再提出して復旧）

export type StatusBadgeInfo = {
  variant: StatusBadgeVariant
  hasWarning: boolean
  /** true の時は variant のラベルのまま neutral 色で出す (取引相手間ミスマッチ解消済みの不整合)。 */
  muted?: boolean
}

/**
 * proto の生 status から UI バッジ情報を導出する純関数。
 * 表示ラベルは i18n のため StatusBadge component が variant から t() で引く
 * (= 純関数の model 層に文言を直書きしない)。
 *
 * 導出ルール:
 * - INCONSISTENT → 不整合警告 (デザイナー指摘 2026-09-14「黄色い系の badge も
 *     同じく ⚠️ アイコンを表示したい」)。 不整合・承認待ちは negative ではないが、
 *     いずれも**業務者の対応が要る状態**なので、 状態ラベルより先に目が行くよう
 *     ⚠️ を出す (`hasWarning` はアイコンの出し分けにしか使われておらず、
 *     保存ブロックや件数には影響しない)
 * - ACCEPTED → 受領済み
 * - SUBMITTED → 提出済
 * - GENERATION_FAILED → 作成失敗警告 (creationFailed)
 * - REJECTED → 差戻し警告 (rejected)
 *   ※ 計画提出画面は read only のため「編集中」概念は廃止。
 *     提出失敗・差戻し状態は再作成を促すため個別バッジで警告として可視化する
 *     (spec docs/specs/plan-submit/procurement-plan-list.md AC-008 準拠: 6 値のバッジ
 *     のうち「差戻し」「提出失敗」は別バッジ)。
 * - GENERATING → 作成中（backend worker が計画値を組み立て中）
 * - GENERATED → 未提出（version に関係なく未提出。backend は計画作成のたびに
 *     version+1 で新規行を残す方針のため、version>1 でも「編集中」にはならない）
 * - SUBMITTING → 処理中（提出 enqueue 済み・OCCTO put 完了待ち。進行表現、警告なし）
 * - SUBMIT_FAILED → 提出失敗（提出起因の permanent 失敗。GENERATION_FAILED
 *     (作成失敗) とはラベル・variant とも別 — XML ダウンロード活性等の判定が
 *     status 1 値で分岐できるよう区別する）
 * - EDITING → 未提出（spec「編集中 ⊂ 未提出」の明示マップ。backend の
 *     PlanSubmissionStatus.SameListDisplayStatus (valueobject) も GENERATED と
 *     EDITING を同じ表示値として最終更新日時の更新判定に使うので、ここを変える時は
 *     そちらも同時に直す。本画面は read-only の
 *     ため draft 概念が実際には発生しないが、他 kind 由来の値が来ても安全に
 *     unsubmitted へ倒す）
 */
export function deriveStatusBadge(
  status: PlanSubmissionStatus,
): StatusBadgeInfo {
  switch (status) {
    case PlanSubmissionStatus.INCONSISTENT:
      return { variant: 'inconsistent', hasWarning: true }
    case PlanSubmissionStatus.ACCEPTED:
      return { variant: 'accepted', hasWarning: false }
    case PlanSubmissionStatus.SUBMITTED:
      return { variant: 'submitted', hasWarning: false }
    case PlanSubmissionStatus.GENERATION_FAILED:
      return { variant: 'creationFailed', hasWarning: true }
    case PlanSubmissionStatus.REJECTED:
      return { variant: 'rejected', hasWarning: true }
    case PlanSubmissionStatus.GENERATING:
      return { variant: 'generating', hasWarning: false }
    case PlanSubmissionStatus.SUBMITTING:
      return { variant: 'submitting', hasWarning: false }
    case PlanSubmissionStatus.APPROVAL_PENDING:
      return { variant: 'approvalPending', hasWarning: true }
    case PlanSubmissionStatus.SUBMIT_FAILED:
      return { variant: 'submitFailed', hasWarning: true }
    case PlanSubmissionStatus.SEND_FAILED:
      return { variant: 'sendFailed', hasWarning: true }
    case PlanSubmissionStatus.GENERATED:
      return { variant: 'unsubmitted', hasWarning: false }
    case PlanSubmissionStatus.EDITING:
      return { variant: 'unsubmitted', hasWarning: false }
    default:
      return { variant: 'unsubmitted', hasWarning: false }
  }
}

/**
 * ステータス badge の表示ラベル i18n key（variant → key）。
 *
 * GenerationSalesPlanList / GenerationSalesPlanDetail / ProcurementPlanList /
 * ProcurementPlanDetail の 4 画面がそれぞれ page 固有 namespace で同一文言の
 * map を持っていた（コピペ）。ja 文言はすべて同一のため
 * `page:planSubmission.statusBadge.*`（画面横断グループ）に集約する。
 *
 * spec AC-008 (procurement-plan-list.md) の 6 値バッジ分離に合わせて
 * `creationFailed` (計画生成失敗) と `rejected` (OCCTO 差戻し) を別 key で扱う。
 */
export const STATUS_BADGE_LABEL_KEYS: Record<StatusBadgeVariant, string> = {
  unsubmitted: 'page:planSubmission.statusBadge.unsubmitted',
  generating: 'page:planSubmission.statusBadge.generating',
  creationFailed: 'page:planSubmission.statusBadge.creationFailed',
  rejected: 'page:planSubmission.statusBadge.rejected',
  submitted: 'page:planSubmission.statusBadge.submitted',
  accepted: 'page:planSubmission.statusBadge.accepted',
  inconsistent: 'page:planSubmission.statusBadge.inconsistent',
  submitting: 'page:planSubmission.statusBadge.submitting',
  approvalPending: 'page:planSubmission.statusBadge.approvalPending',
  submitFailed: 'page:planSubmission.statusBadge.submitFailed',
  sendFailed: 'page:planSubmission.statusBadge.sendFailed',
}
